"use client";

import { useEffect, useState } from "react";
import { useAuth } from "@clerk/nextjs";
import { apiFetch } from "@/lib/api";

interface ApiKey {
  id: number;
  name: string;
  prefix: string;
  created_at: string;
  last_used_at: string | null;
}

/**
 * Personal API keys (GET/POST /keys, DELETE /keys/{id}) as a view tab: list
 * active keys, generate a new one (the plaintext is shown exactly once, at
 * creation), and revoke.
 */
export default function ApiKeysPanel() {
  const { getToken } = useAuth();
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState<{ name: string; key: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  async function listKeys(): Promise<ApiKey[]> {
    const res = await apiFetch("/keys", { token: (await getToken()) ?? undefined });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return ((await res.json()) as { keys: ApiKey[] }).keys;
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await listKeys();
        if (!cancelled) setKeys(items);
      } catch (e) {
        if (!cancelled) {
          setError(`Kunde inte hämta API-nycklar (${e instanceof Error ? e.message : e}).`);
          setKeys([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getToken]);

  async function generate() {
    if (creating) return;
    setCreating(true);
    setError(null);
    try {
      const res = await apiFetch("/keys", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newName.trim() || "key" }),
        token: (await getToken()) ?? undefined,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { key: string; name: string };
      setNewKey(data);
      setCopied(false);
      setNewName("");
      setKeys(await listKeys());
    } catch (e) {
      setError(`Kunde inte generera nyckel (${e instanceof Error ? e.message : e}).`);
    } finally {
      setCreating(false);
    }
  }

  async function copyNewKey() {
    if (!newKey) return;
    try {
      await navigator.clipboard.writeText(newKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Kunde inte kopiera till urklipp — markera och kopiera manuellt.");
    }
  }

  async function revoke(id: number) {
    if (!window.confirm("Ändra denna API-nyckel? Den slutar fungera omedelbart.")) return;
    setBusyId(id);
    try {
      const res = await apiFetch(`/keys/${id}`, { method: "DELETE", token: (await getToken()) ?? undefined });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setKeys((keys ?? []).filter((k) => k.id !== id));
    } catch (e) {
      setError(`Kunde inte ändra nyckeln (${e instanceof Error ? e.message : e}).`);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="apikey-panel">
      {error && <div className="callout callout--boxed callout--warn error-box">{error}</div>}

      {newKey && (
        <div className="callout callout--boxed apikey-new">
          <p className="apikey-new-label">Ny nyckel skapad: {newKey.name}</p>
          <div className="apikey-new-row">
            <code className="apikey-new-key">{newKey.key}</code>
            <button className="btn btn-outline" onClick={copyNewKey} disabled={copied}>
              {copied ? "Kopierad ✓" : "Kopiera"}
            </button>
          </div>
          <p className="apikey-warn">Spara nyckeln nu — den visas endast en gång.</p>
        </div>
      )}

      <div className="apikey-create">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Namn (valfritt), t.ex. ”Min server”"
        />
        <button className="btn" onClick={generate} disabled={creating}>
          {creating ? "Genererar…" : "Generera ny nyckel"}
        </button>
      </div>

      {keys === null && <p className="empty">Hämtar lista…</p>}
      {keys !== null && keys.length === 0 && !newKey && !error && (
        <p className="empty">Inga API-nycklar ännu.</p>
      )}
      <div className="apikey-list card-grid">
        {keys?.map((k) => (
          <div className="card card--sm card--flat source-item cite-card saved-card" key={k.id}>
            <span className="saved-q">{k.name}</span>
            <div className="meta saved-meta">
              <span className="saved-date">
                {k.prefix}
                {" · skapad "}
                {new Date(k.created_at).toLocaleDateString("sv-SE", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
                {k.last_used_at
                  ? ` · senast använd ${new Date(k.last_used_at).toLocaleDateString("sv-SE", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}`
                  : " · aldrig använd"}
              </span>
              <button
                className="btn-bare btn-danger"
                aria-label="Ändra nyckel"
                title="Ändra nyckel"
                onClick={() => revoke(k.id)}
                disabled={busyId === k.id}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                Ta bort
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
